<template>
  
  <div
    ref     = "track"
    class   = "track"
    @click  = "onJump"
  >

    <div
      v-for   = "(segment, index) in bufferedSegments"
      :key    = "index"
      class   = "buffered-segment"
      :style  = "{ left: segment.left + 'px', width: segment.right - segment.left + 'px' }"
    >
    </div>

    <div 
      class       = "scrubber elevation-6"
      :class      = "{ scrubbing: isScrubbing }"
      :style      = "scrubberStyle"
      @mousedown  = "onScrubbingStarted"
    >
    </div>

  </div>

</template>

<script>

// #region Imports

/* vuex */
import { mapState } from 'vuex';

/* Telestream */
import { JUMP_TO_TIME, PLAY_VIDEO, PAUSE_VIDEO } from '@/utils/action-types';

// #endregion Imports

// #region Constants

const SCRUBBER_WIDTH                = 14;
const BUFFER_POLLING_INTERVAL_MS    = 500;

// #endregion Constants

export default {

  // #region Properties

  name: 'ScopeBufferBar',
  data() {
    return {
      trackWidth:          0,
      trackWidthObserver:  undefined,
      isScrubbing:         false,
      relativeScrubOffset: 0,
      playAfterScrubbing:  false,
      buffered:            [],
      bufferPollingHandle: undefined
    };
  },
  computed: {
    ...mapState([
      'scopes',
      'video'
    ]),
    scrubberStyle() {
      return {
        transform: `translateX(${((this.video.currentTime /  this.video.duration) * this.trackWidth) - (SCRUBBER_WIDTH / 2)}px)`
      };
    },
    bufferedSegments() {
      return this.buffered.map(s => this.calculateSegmentOffset(s.start, s.end));
    }
  },

  // #endregion Properties

  // #region Life-Cycle

  mounted() {
    /*
     * Note: To make sure what is buffered shows up both here and in Safari, instead
     * of checking the "buffered" property on the glim video controller, this component
     * just checks the native video element every once in a while.
     */
    this.bufferPollingHandle = setInterval(
      () => {
        const segments = [];
        for (let segmentIndex = 0; segmentIndex < this.scopes.element.buffered.length; segmentIndex++) {
          segments.push({
            start: this.scopes.element.buffered.start(segmentIndex),
            end:   this.scopes.element.buffered.end(segmentIndex)
          });        
        }

        this.buffered = segments;
      }, 
      BUFFER_POLLING_INTERVAL_MS
    );

    if (this.$refs.track) {
      this.trackWidth = this.$refs.track.offsetWidth;
      this.trackWidthObserver = new ResizeObserver(this.onTrackResized).observe(this.$refs.track);
    }
  },

  beforeDestroy() {
    if (this.bufferPollingHandle) {
      clearInterval(this.bufferPollingHandle);
    }

    if (this.trackWidthObserver) {
      this.trackWidthObserver.unobserve(this.$refs.track);
    }
  },

  // #endregion Life-Cycle

  // #region Operations

  methods: {

    // #region Events

    onTrackResized() {
      if (this.$refs.track) {
        this.trackWidth = this.$refs.track.offsetWidth;
      }
    },

    onJump(event) {
      this.relativeScrubOffset = this.calculateEventOffset(event);
      this.$store.dispatch(JUMP_TO_TIME, this.relativeScrubOffset * this.video.duration);
    },

    onScrubbingStarted(event) {
      this.relativeScrubOffset = this.calculateEventOffset(event);
      this.isScrubbing = true;

      window.addEventListener('mouseup', this.onScrubbingEnded);
      window.addEventListener('mousemove', this.onScrubbing);
      document.body.style.cursor = 'grabbing';

      this.$store.dispatch(PAUSE_VIDEO);
      this.playAfterScrubbing = !this.video.paused;
    },

    onScrubbing(event) {
      if (!this.isScrubbing) {
        return;
      }

      event.preventDefault();

      this.relativeScrubOffset = this.calculateEventOffset(event);
      this.cursorOffset = this.relativeScrubOffset * this.trackWidth;
      this.$store.dispatch(JUMP_TO_TIME, this.relativeScrubOffset * this.video.duration);
    },

    onScrubbingEnded() {
      if (!this.isScrubbing) {
        return;
      }

      this.isScrubbing = false;
      window.removeEventListener('mousemove', this.onScrubbing);
      window.removeEventListener('mouseup', this.onScrubbingEnded);
      document.body.style.cursor = 'default';

      if (this.playAfterScrubbing) {
        this.$store.dispatch(PLAY_VIDEO);
      }
    },

    // #endregion Events

    // #region Utilities

    calculateEventOffset(event) {
      const trackBounds = this.$refs.track.getBoundingClientRect();
      const absoluteLeftOffset = event.clientX - trackBounds.x;
      const relativeLeftOffset = absoluteLeftOffset / trackBounds.width;
      return Math.max(0, Math.min(relativeLeftOffset, 1));
    },

    calculateSegmentOffset(start, end) {
      const relativeLeftOffset = start / this.video.duration;
      const relativeRightOffset = end / this.video.duration;

      return {
        left:  relativeLeftOffset * this.trackWidth,
        right: relativeRightOffset * this.trackWidth
      };
    }

    // #endregion Utilities

  }

  // #endregion Operations

}

</script>

<style lang="scss" scoped>

@import '../../../styles/colors.scss';

$track-color:         #151515;
$buffered-color:      #3C3C3C;
$scrubber-color:      $label-color;

.track {
  height:             6px;
  background-color:   $track-color;
  position:           relative;

  .buffered-segment {
    background-color:   $buffered-color;

    position:           absolute;
    top:                0;
    bottom:             0;
  }

  .scrubber {
    width:              14px;
    height:             14px;
    margin-top:         -4px;
    border-radius:      9999px;

    background:         $scrubber-color;

    cursor:             grab;
    &.scrubbing {
      cursor:             grabbing;
    }
  }
}

</style>